import type {
  ParticleDatum,
  ParticleLayoutContext,
  ParticleLayoutFactory,
  ParticleLayoutName,
  ParticleTarget,
} from './types';

function hash(value: number, salt: number): number {
  let result = value ^ (salt * 0x9e3779b9);
  result = Math.imul(result ^ (result >>> 16), 0x21f0aaad);
  result = Math.imul(result ^ (result >>> 15), 0x735a2d97);
  return ((result ^ (result >>> 15)) >>> 0) / 4294967295;
}

export const fieldLayout: ParticleLayoutFactory = (particles) =>
  particles.map(({ id }) => ({
    id,
    x: 0.06 + hash(id, 1) * 0.88,
    y: 0.08 + hash(id, 2) * 0.82,
    radius: 1.45 + hash(id, 3) * 0.7,
  }));

export const gridLayout: ParticleLayoutFactory = (particles, context) => {
  const aspect = Math.max(context.width, 1) / Math.max(context.height, 1);
  const columns = Math.ceil(Math.sqrt(particles.length * aspect));
  const rows = Math.ceil(particles.length / columns);
  return particles.map(({ id }, index) => ({
    id,
    x: columns === 1 ? 0.5 : 0.08 + (index % columns) / (columns - 1) * 0.84,
    y: rows === 1 ? 0.5 : 0.12 + Math.floor(index / columns) / (rows - 1) * 0.76,
    radius: 1.55,
  }));
};

function numericBandValue(particle: ParticleDatum, field: keyof ParticleDatum['attributes']): number {
  const value = particle.attributes[field];
  if (typeof value === 'number') return Math.max(value, 0);
  if (Array.isArray(value)) return value.length > 0 ? 1 : 0;
  return 0;
}

export const bandsLayout: ParticleLayoutFactory = (particles, context) => {
  const field = context.bandField ?? 'gender';
  const groups = new Map<number, ParticleDatum[]>();
  for (const particle of particles) {
    const value = numericBandValue(particle, field);
    const group = groups.get(value) ?? [];
    group.push(particle);
    groups.set(value, group);
  }
  const keys = [...groups.keys()].sort((a, b) => a - b);
  const targets: ParticleTarget[] = [];
  for (const [bandIndex, key] of keys.entries()) {
    const group = groups.get(key) ?? [];
    const columns = Math.max(1, Math.ceil(Math.sqrt(group.length * 1.8)));
    const rows = Math.max(1, Math.ceil(group.length / columns));
    for (const [index, particle] of group.entries()) {
      const bandWidth = 0.86 / Math.max(keys.length, 1);
      const bandStart = 0.07 + bandIndex * bandWidth;
      targets.push({
        id: particle.id,
        x: bandStart + bandWidth * (0.12 + (index % columns) / Math.max(columns - 1, 1) * 0.76),
        y: 0.16 + Math.floor(index / columns) / Math.max(rows - 1, 1) * 0.68,
        radius: 1.55,
      });
    }
  }
  return targets;
};

export const postalLayout: ParticleLayoutFactory = (particles) =>
  particles.map((particle) => {
    const prefix = Number(particle.attributes.postal ?? 0);
    const local = prefix === 559;
    const kansai = prefix >= 520 && prefix <= 699;
    const angle = hash(particle.id, 41) * Math.PI * 2;
    const radial = Math.sqrt(hash(particle.id, 42));
    const centerX = local ? 0.52 : kansai ? 0.51 : 0.5;
    const centerY = local ? 0.55 : kansai ? 0.5 : 0.48;
    const spreadX = local ? 0.08 : kansai ? 0.32 : 0.44;
    const spreadY = local ? 0.1 : kansai ? 0.28 : 0.38;
    return {
      id: particle.id,
      x: centerX + Math.cos(angle) * radial * spreadX,
      y: centerY + Math.sin(angle) * radial * spreadY,
      radius: local ? 1.9 : 1.48,
    };
  });

export const particleLayouts: Record<ParticleLayoutName, ParticleLayoutFactory> = {
  field: fieldLayout,
  grid: gridLayout,
  bands: bandsLayout,
  postal: postalLayout,
  companion: (particles, context) => bandsLayout(particles, { ...context, bandField: 'companion' }),
  purpose: (particles, context) => bandsLayout(particles, { ...context, bandField: 'visit_purpose_main' }),
  satisfaction: (particles, context) => bandsLayout(particles, { ...context, bandField: 'satisfaction' }),
  'office-awareness': (particles, context) => bandsLayout(particles, { ...context, bandField: 'office_awareness' }),
  geographic: postalLayout,
  'full-population': gridLayout,
};

export function createParticleData(rows: readonly ParticleDatum['attributes'][]): ParticleDatum[] {
  return rows.map((row) => ({ id: row.id, attributes: row }));
}

export function resolveLayout(
  name: ParticleLayoutName,
  particles: readonly ParticleDatum[],
  context: ParticleLayoutContext,
): ParticleTarget[] {
  return particleLayouts[name](particles, context);
}

export function capDevicePixelRatio(value: number): number {
  return Math.max(1, Math.min(value || 1, 2));
}
